import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import VoiceInputButton from '../../../components/ui/VoiceInputButton';
import { sanitizeInput } from '../../../utils/sanitizer';

import { symptomService } from '../../../services/symptomService';

const NotesAndVoiceInput = ({ notes, onNotesChange }) => {
  const [lastEntry, setLastEntry] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);

  useEffect(() => {
    // Fetch last entry for templates
    fetchLastEntry();
  }, []);

  const fetchLastEntry = async () => {
    const { data } = await symptomService?.getEntries({ limit: 1 });
    if (data && data?.length > 0) {
      setLastEntry(data?.[0]);
    }
  };

  const applyTemplate = (template) => {
    let templateText = '';
    
    switch (template) {
      case 'same':
        if (lastEntry?.notes) {
          templateText = `Same as yesterday: ${lastEntry?.notes}`;
        } else {
          templateText = 'Same symptoms as yesterday';
        }
        break;
      case 'repeat':
        if (lastEntry?.notes) {
          templateText = lastEntry?.notes;
        } else {
          templateText = 'Repeat of previous symptoms';
        }
        break;
      case 'better':
        templateText = 'Symptoms are improving compared to yesterday. Feeling better overall.';
        break;
      case 'worse':
        templateText = 'Symptoms have worsened since yesterday. More discomfort than before.';
        break;
      case 'morning':
        templateText = 'Symptoms started this morning after waking up.';
        break;
      case 'evening':
        templateText = 'Symptoms developed later in the day, around evening time.';
        break;
      default:
        templateText = '';
    }

    onNotesChange(sanitizeInput(templateText, { maxLength: 5000, allowHtml: false, removeControlChars: false }));
    setShowTemplates(false);
  };

  const handleNotesChange = (e) => {
    const rawValue = e?.target?.value;
    // Sanitize input to prevent XSS
    const sanitized = sanitizeInput(rawValue, {
      maxLength: 5000,
      allowHtml: false,
      removeControlChars: false // Allow line breaks in textarea
    });
    onNotesChange(sanitized);
  };

  const handleVoiceChange = (text) => {
    onNotesChange(text);
  };

  const templates = [
    { id: 'same', label: 'Same as Yesterday', icon: 'Copy' },
    { id: 'repeat', label: 'Repeat Last Entry', icon: 'RotateCcw' },
    { id: 'better', label: 'Feeling Better', icon: 'TrendingUp' },
    { id: 'worse', label: 'Feeling Worse', icon: 'TrendingDown' },
    { id: 'morning', label: 'Started This Morning', icon: 'Sunrise' },
    { id: 'evening', label: 'Started This Evening', icon: 'Sunset' }
  ];

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg md:text-xl lg:text-2xl font-heading font-bold text-foreground mb-2">
          Additional Information
        </h3>
        <p className="text-sm md:text-base text-muted-foreground">
          Add any extra details that might help (optional)
        </p>
      </div>

      {/* Quick Templates */}
      <div className="bg-card rounded-xl p-4 border border-border">
        <button
          onClick={() => setShowTemplates(!showTemplates)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center gap-2">
            <Icon name="Zap" size={20} className="text-primary" />
            <span className="text-sm md:text-base font-medium text-foreground">
              Quick Symptom Templates
            </span>
          </div>
          <Icon name={showTemplates ? 'ChevronUp' : 'ChevronDown'} size={20} className="text-muted-foreground" />
        </button>
        
        {showTemplates && (
          <div className="grid grid-cols-2 gap-2 mt-4">
            {templates?.map((template) => (
              <button
                key={template?.id}
                onClick={() => applyTemplate(template?.id)}
                className="flex items-center gap-2 p-3 rounded-lg border border-border bg-background hover:bg-muted hover:border-primary transition-all text-left"
              >
                <Icon name={template?.icon} size={16} className="text-primary flex-shrink-0" />
                <span className="text-xs md:text-sm text-foreground">{template?.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
          <div className="flex items-center justify-between mb-3">
            <label htmlFor="notes-input" className="block text-sm md:text-base font-medium text-foreground">
              Written Notes
            </label>
            <VoiceInputButton
              value={notes || ''}
              onChange={handleVoiceChange}
              maxLength={5000}
            />
          </div>

          <textarea
            id="notes-input"
            value={notes}
            onChange={handleNotesChange}
            placeholder="Describe anything else you think is important..."
            rows="4"
            maxLength={5000}
            className="
              w-full px-4 py-3 rounded-lg border border-border bg-background
              text-foreground placeholder:text-muted-foreground
              focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring
              transition-smooth resize-none text-sm md:text-base
            "
            aria-label="Additional notes"
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs md:text-sm text-muted-foreground">
              Is there anything worrying you about these symptoms?
            </p>
            <p className="text-xs text-muted-foreground">
              {notes?.length || 0}/5000
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotesAndVoiceInput;